/**
 * Pieces shared by the roles and groups services (both are named collections whose members
 * live on the member records). Kept in one place so the two services cannot drift apart.
 */
import { clone, errorEnvelope, notFoundEnvelope } from '../shared/utils/mockServiceHelpers.js';
import {
  addMembership,
  listMembership,
  membersOf,
  previewOf,
  removeMembership,
  unknownMemberIds,
} from './membershipHelpers.js';

/** Case-insensitive name clash inside `collection`, ignoring the item `exceptId`. */
export const isNameTaken = (collection, name, exceptId) =>
  collection.some(
    (item) => item.id !== exceptId && item.name.trim().toLowerCase() === String(name).trim().toLowerCase()
  );

/** 422 envelope when any of `member_ids` is unknown, else null. */
export const memberRefError = (member_ids = []) => {
  const missing = unknownMemberIds(member_ids);
  return missing.length
    ? errorEnvelope(422, 'UNKNOWN_MEMBER', `Unknown member: ${missing.join(', ')}`)
    : null;
};

/** A copy of the role/group with its live `member_count` and `members_preview`. */
export const withMembers = (key, item) => ({
  ...clone(item),
  member_count: membersOf(key, item.id).length,
  members_preview: previewOf(key, item.id),
});

/** `{ list, add, remove }` membership operations of a role/group, with the not-found guard. */
export const membershipApi = (key, find, notFoundMessage) => ({
  list: (id, params) =>
    Promise.resolve(find(id) ? listMembership(key, id, params) : notFoundEnvelope(notFoundMessage)),
  add: (id, member_ids) =>
    Promise.resolve(find(id) ? addMembership(key, id, member_ids) : notFoundEnvelope(notFoundMessage)),
  remove: (id, member_id) =>
    Promise.resolve(
      find(id) ? removeMembership(key, id, member_id) : notFoundEnvelope(notFoundMessage)
    ),
});
